<script setup lang="ts">
import { computed, toRef } from "vue";
import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import type { AnalysisCount, ChartCard, SankeyData } from "@/types";
import { POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";
import { cardCodes, useAnalysisContext, useCardPopulation } from "../analysisData";
import ResultsSankeyChart, { type SankeyNode } from "../Charts/ResultsSankeyChart.vue";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import PopulationSwitch from "./PopulationSwitch.vue";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const card = toRef(props, "card");
const { weighted, availableCards, displayLocale } = useAnalysisContext();
const { choices, selected, filters } = useCardPopulation(card);

const sourceQuestionId = computed<number | null>(() => props.card.content?.source_question_id || null);
const targetQuestionId = computed<number | null>(() => props.card.content?.target_question_id || null);

// An empty answer selection means every answer; the service narrows the pairs, so the
// base and the node counts follow the selection.
const body = computed(() => {
  if (!sourceQuestionId.value || !targetQuestionId.value) return null;
  return {
    source_question_id: sourceQuestionId.value,
    target_question_id: targetQuestionId.value,
    source_codes: props.card.content?.source_codes || [],
    target_codes: props.card.content?.target_codes || [],
    filters: filters.value,
  };
});

const { result, loading, state } = useAnalysisQuery<SankeyData>("sankey", body);

const respondents = computed(() => {
  if (!result.value) return null;
  return weighted.value ? result.value.weighted_respondents : result.value.respondents;
});

function metaFor(questionId: number | null): ChartCard | null {
  if (!questionId) return null;
  return availableCards.value.find((c: ChartCard) => (c.questions || []).some((q) => q.id === questionId)) || null;
}

const sourceMeta = computed(() => metaFor(sourceQuestionId.value));
const targetMeta = computed(() => metaFor(targetQuestionId.value));

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

// Closed answers, or the scale of an nps / rating / matrix question.
const labelsFor = (meta: ChartCard | null) => Object.fromEntries(cardCodes(meta, resolveTitle).map(({ code, label }) => [code, label]));

// "Q3 · Item" when the picked question is one item of a matrix or a loop.
function sideLabel(meta: ChartCard | null, questionId: number | null) {
  if (!meta) return `Q${questionId}`;
  const question = (meta.questions || []).find((q) => q.id === questionId);
  const item = resolveTitle(question?.item_title);
  return item ? `${meta.name} · ${item}` : meta.name || `Q${questionId}`;
}

const NODE_COLORS = [
  POPULATION_COLOR_PALETTES.blue[500],
  POPULATION_COLOR_PALETTES.teal[500],
  POPULATION_COLOR_PALETTES.purple[500],
  POPULATION_COLOR_PALETTES.amber[500],
  POPULATION_COLOR_PALETTES.rose[500],
  POPULATION_COLOR_PALETTES.blue[800],
  POPULATION_COLOR_PALETTES.teal[800],
  POPULATION_COLOR_PALETTES.purple[800],
  POPULATION_COLOR_PALETTES.amber[800],
];
const MAX_NODES = 9;
const OTHER = "__other__";

// The biggest answers of each side keep their node, the tail folds into "Other":
// a 30-brand question would otherwise turn the diagram into spaghetti.
const chart = computed(() => {
  const data = result.value;
  const count = (value: AnalysisCount | undefined) => (value ? (weighted.value ? value.weighted_nb : value.nb) : 0);
  const links = (data?.links || [])
    .map((link) => ({ source: link.source, target: link.target, nb: count(link) }))
    .filter((link) => link.nb > 0);
  if (!links.length) return null;

  const buildSide = (key: "source" | "target", labels: Record<string, string>) => {
    // A node is sized and labelled by its respondents; the links through it (its flow)
    // can sum higher on a multiple-choice side and only share out the ribbons inside it.
    const flows = new Map<string, number>();
    links.forEach((link) => flows.set(link[key], (flows.get(link[key]) || 0) + link.nb));
    const persons = (code: string) => count(data?.nodes?.[key]?.[code]) || flows.get(code) || 0;
    const sorted = [...flows.keys()].map((code) => [code, persons(code)] as const).sort((a, b) => b[1] - a[1]);
    const kept = new Set(sorted.slice(0, MAX_NODES).map(([code]) => code));
    const nodes: SankeyNode[] = sorted
      .filter(([code]) => kept.has(code))
      .map(([code, total], idx) => ({
        code,
        label: labels[code] || code,
        total: Math.round(total),
        flow: Math.round(flows.get(code) || 0),
        color: NODE_COLORS[idx % NODE_COLORS.length],
      }));
    if (sorted.length > MAX_NODES) {
      // Folded answers may share respondents, so "Other" is capped by the base.
      const rest = sorted.slice(MAX_NODES);
      const otherTotal = Math.min(rest.reduce((sum, [, total]) => sum + total, 0), respondents.value || Infinity);
      const otherFlow = rest.reduce((sum, [code]) => sum + (flows.get(code) || 0), 0);
      nodes.push({
        code: OTHER,
        label: "Other",
        total: Math.round(otherTotal),
        flow: Math.round(otherFlow),
        color: POPULATION_COLOR_PALETTES.slate[300],
      });
    }
    return { nodes, kept };
  };

  const sourceSide = buildSide("source", labelsFor(sourceMeta.value));
  const targetSide = buildSide("target", labelsFor(targetMeta.value));

  const folded = new Map<string, number>();
  links.forEach((link) => {
    const source = sourceSide.kept.has(link.source) ? link.source : OTHER;
    const target = targetSide.kept.has(link.target) ? link.target : OTHER;
    const key = `${source}→${target}`;
    folded.set(key, (folded.get(key) || 0) + link.nb);
  });
  const foldedLinks = [...folded.entries()].map(([key, nb]) => {
    const [source, target] = key.split("→");
    return { source, target, nb: Math.round(nb) };
  });

  return { sources: sourceSide.nodes, targets: targetSide.nodes, links: foldedLinks };
});

const cardState = computed(() => state(!!chart.value));
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="respondents"
    :weighted="weighted"
    respondents-hint="Respondents who answered both questions, the base of every share."
    description="Where the respondents of each answer on the left end up on the right: switching between brands, before / after a stimulus. A multiple-choice question sends a respondent along one ribbon per answer."
    :state="cardState"
    state-message="Select a source and a target question to draw the flow.">
    <template #subtitle>
      <p v-if="sourceQuestionId && targetQuestionId" class="text-xs text-muted-foreground">
        {{ sideLabel(sourceMeta, sourceQuestionId) }} → {{ sideLabel(targetMeta, targetQuestionId) }}
      </p>
    </template>
    <template #toolbar>
      <PopulationSwitch v-if="choices.length > 1" :card="card" :populations="choices" :selected="selected" />
    </template>

    <div v-if="chart" :class="{ 'opacity-50': loading }">
      <ResultsSankeyChart
        :sources="chart.sources"
        :targets="chart.targets"
        :links="chart.links"
        :respondents="respondents || 0" />
    </div>
  </AnalysisCardShell>
</template>
